import { ArrowLeftRight, Bell, BookOpen, CreditCard, Heart, Home, RotateCcw, Search, Settings, ShoppingCart, Truck } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import DashboardFrame, { type DashboardNavItem } from '../components/DashboardFrame';
import PaymentSplitInfo from '../components/PaymentSplitInfo';
import { paymentService } from '../../../shared/services';
import { useShop } from '../../../shared/store';
import type { PaymentHistoryEntry } from '../../../shared/services/paymentService';

const navItemsConfig: Array<Omit<DashboardNavItem, 'label'> & { labelKey?: string; label?: string }> = [
  { id: 'home', labelKey: 'dashboardBuyer.nav.home', to: '/dashboard/home', icon: Home },
  { id: 'library', labelKey: 'dashboardBuyer.nav.library', to: '/dashboard/library', icon: BookOpen },
  { id: 'wishlist', labelKey: 'dashboardBuyer.nav.wishlist', to: '/dashboard/wishlist', icon: Heart },
  { id: 'cart', labelKey: 'dashboardBuyer.nav.cart', to: '/dashboard/cart', icon: ShoppingCart },
  { id: 'tracking', labelKey: 'dashboardBuyer.nav.tracking', to: '/dashboard/tracking', icon: Truck },
  { id: 'transfers', label: 'My Transfers', to: '/dashboard/transfers', icon: ArrowLeftRight },
  { id: 'transactions', label: 'Transactions', to: '/dashboard/transactions', icon: CreditCard },
  { id: 'refunds', labelKey: 'dashboardBuyer.nav.refunds', to: '/dashboard/refunds', icon: RotateCcw },
  { id: 'notifications', labelKey: 'dashboardBuyer.nav.notifications', to: '/dashboard/notifications', icon: Bell },
  { id: 'settings', labelKey: 'dashboardBuyer.nav.settings', to: '/dashboard/settings', icon: Settings },
];

interface FilterOptions {
  status: 'all' | 'successful' | 'pending' | 'failed';
  searchValue: string;
  sortBy: 'newest' | 'oldest' | 'highest' | 'lowest';
}

const formatCurrency = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-RW', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(0)}`;
  }
};

const formatDate = (value: string): string =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));

const getStatusColor = (status: string) => {
  const normalized = normalizeTransactionStatus(status);
  if (normalized === 'successful') return 'bg-emerald-100 text-emerald-700';
  if (normalized === 'pending') return 'bg-amber-100 text-amber-700';
  return 'bg-rose-100 text-rose-700';
};

const normalizeTransactionStatus = (status: string): 'successful' | 'pending' | 'failed' => {
  const normalized = status.trim().toLowerCase();
  if (['successful', 'success', 'paid', 'completed', 'succeeded'].includes(normalized)) {
    return 'successful';
  }
  if (['failed', 'cancelled', 'canceled', 'declined', 'error'].includes(normalized)) {
    return 'failed';
  }
  return 'pending';
};

export const BuyerTransactionHistoryPage = () => {
  const { t } = useTranslation();
  const { section = 'transactions' } = useParams<{ section?: string }>();
  const { currentUser } = useShop();

  const [transactions, setTransactions] = useState<PaymentHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<FilterOptions>({
    status: 'all',
    searchValue: '',
    sortBy: 'newest',
  });
  const [selectedTransaction, setSelectedTransaction] = useState<PaymentHistoryEntry | null>(null);

  const belongsToBuyer = useCallback(
    (tx: PaymentHistoryEntry): boolean => {
      if (!currentUser) {
        return true;
      }

      const buyerId = currentUser.id?.trim();
      const buyerEmail = currentUser.email?.trim().toLowerCase();

      const txUserId = tx.user?.id?.trim();
      const txUserEmail = tx.user?.email?.trim().toLowerCase();
      const txCustomerEmail = tx.customer?.email?.trim().toLowerCase();

      const matchesById = Boolean(buyerId && txUserId && txUserId === buyerId);
      const matchesByEmail = Boolean(
        buyerEmail && ((txUserEmail && txUserEmail === buyerEmail) || (txCustomerEmail && txCustomerEmail === buyerEmail)),
      );

      if (matchesById || matchesByEmail) {
        return true;
      }

      // If the API didn't include any buyer identifiers, avoid hiding rows.
      if (!txUserId && !txUserEmail && !txCustomerEmail) {
        return true;
      }

      return false;
    },
    [currentUser],
  );

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await paymentService.getBuyerTransactions();

      if (result.success && result.data) {
        setTransactions(result.data.filter((tx) => belongsToBuyer(tx)));
      } else {
        setError('Failed to load transaction history');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [belongsToBuyer]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const filteredTransactions = useMemo(() => {
    let result = [...transactions];

    // Filter by status
    if (filters.status !== 'all') {
      result = result.filter((tx) => normalizeTransactionStatus(tx.status) === filters.status);
    }

    // Filter by search
    if (filters.searchValue) {
      const search = filters.searchValue.toLowerCase();
      result = result.filter(
        (tx) =>
          tx.customer.name.toLowerCase().includes(search) ||
          tx.customer.email.toLowerCase().includes(search) ||
          tx.transactionRef.toLowerCase().includes(search) ||
          tx.id.toLowerCase().includes(search),
      );
    }

    // Sort
    result.sort((a, b) => {
      if (filters.sortBy === 'newest') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      if (filters.sortBy === 'oldest') {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      if (filters.sortBy === 'highest') {
        return b.amount - a.amount;
      }
      return a.amount - b.amount;
    });

    return result;
  }, [transactions, filters]);

  const stats = useMemo(() => {
    const totalAmount = transactions.reduce((sum, tx) => sum + tx.amount, 0);
    const totalCount = transactions.length;
    const averageTransaction = totalCount > 0 ? totalAmount / totalCount : 0;

    return { totalAmount, totalCount, averageTransaction };
  }, [transactions]);

  const navItems = navItemsConfig.map((config) => ({
    id: config.id,
    label: config.label ?? (config.labelKey ? t(config.labelKey) : config.id),
    to: config.to,
    icon: config.icon,
  }));
  const activeNavId = navItems.some((item) => item.id === section) ? section : 'transactions';

  return (
    <DashboardFrame
      brand={t('dashboardBuyer.brand', 'Haapa Store')}
      roleLabel={t('dashboardBuyer.roleLabel', 'Buyer')}
      userName={currentUser?.name ?? t('dashboardBuyer.defaultUserName', 'Reader')}
      userImageUrl={currentUser?.profileImageUrl}
      pageTitle={t('dashboardBuyer.sections.transactions.title', 'Transaction History')}
      pageDescription={t('dashboardBuyer.sections.transactions.description', 'View all your purchases and payment details')}
      navItems={navItems}
      activeNavId={activeNavId}
      headerVariant="buyer"
      showSettings={false}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="rounded-3xl bg-gradient-to-r from-orange-500 to-orange-600 p-6 text-white shadow-lg sm:p-8">
          <h1 className="text-3xl font-bold">{t('dashboardBuyer.sections.transactions.title', 'Transaction History')}</h1>
          <p className="mt-2 text-orange-100">
            {t('dashboardBuyer.sections.transactions.description', 'View all your purchases and payment details')}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs text-slate-600">Total Purchases</p>
            <p className="mt-2 text-2xl font-bold text-slate-900">{stats.totalCount}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs text-slate-600">Total Spent</p>
            <p className="mt-2 text-2xl font-bold text-slate-900">{formatCurrency(stats.totalAmount, 'RWF')}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs text-slate-600">Average Purchase</p>
            <p className="mt-2 text-2xl font-bold text-slate-900">{formatCurrency(stats.averageTransaction, 'RWF')}</p>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="grid gap-4 lg:grid-cols-4">
          <div className="lg:col-span-3 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by customer, email, or transaction ID..."
                value={filters.searchValue}
                onChange={(e) => setFilters({ ...filters, searchValue: e.target.value })}
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-900 focus:border-orange-400 focus:outline-none"
              />
            </div>

            {/* Transactions List */}
            {loading ? (
              <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white p-12">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-orange-500" />
              </div>
            ) : error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">{error}</div>
            ) : filteredTransactions.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
                <CreditCard className="mx-auto h-12 w-12 text-slate-400" />
                <p className="mt-4 text-slate-600">No transactions match your filters</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTransactions.map((transaction, index) => {
                  const transactionKey = `${transaction.id}-${transaction.transactionRef}-${transaction.createdAt}-${index}`;

                  return (
                  <div
                    key={transactionKey}
                    className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-orange-300 hover:shadow-md sm:p-5"
                    onClick={() => setSelectedTransaction(transaction)}
                  >
                    <div className="flex items-start justify-between gap-4 sm:flex-row">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100">
                            <CreditCard className="h-5 w-5 text-orange-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-slate-900">{transaction.customer.name}</p>
                            <p className="truncate text-sm text-slate-500">{transaction.customer.email}</p>
                          </div>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="text-xs font-medium text-slate-500">ID: {transaction.id}</span>
                          <span className="text-xs font-medium text-orange-600">{transaction.transactionRef}</span>
                          <span className="text-xs text-slate-500">{formatDate(transaction.createdAt)}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-slate-900">{formatCurrency(transaction.amount, transaction.currency)}</p>
                        <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusColor(transaction.status)}`}>
                          {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                )})}
              </div>
            )}
          </div>

          {/* Right Sidebar - Filters & Details */}
          <div className="space-y-4">
            {/* Filters */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-semibold text-slate-900">Filters</h3>

              <div className="mt-4 space-y-3">
                <div>
                  <label className="text-xs font-semibold uppercase text-slate-600">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        status: e.target.value as FilterOptions['status'],
                      })
                    }
                    className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900"
                  >
                    <option value="all">All Statuses</option>
                    <option value="successful">Successful</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase text-slate-600">Sort By</label>
                  <select
                    value={filters.sortBy}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        sortBy: e.target.value as FilterOptions['sortBy'],
                      })
                    }
                    className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="highest">Highest Amount</option>
                    <option value="lowest">Lowest Amount</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Transaction Details */}
            {selectedTransaction && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <h3 className="font-semibold text-slate-900">Transaction Details</h3>

                <div className="mt-4 space-y-3 text-sm">
                  <div className="border-b border-slate-200 pb-3">
                    <p className="text-xs text-slate-600">Transaction ID</p>
                    <p className="mt-1 font-mono font-semibold text-slate-900">{selectedTransaction.id}</p>
                  </div>

                  <div className="border-b border-slate-200 pb-3">
                    <p className="text-xs text-slate-600">Reference</p>
                    <p className="mt-1 font-mono font-semibold text-slate-900">{selectedTransaction.transactionRef}</p>
                  </div>

                  <div className="border-b border-slate-200 pb-3">
                    <p className="text-xs text-slate-600">Order ID</p>
                    <p className="mt-1 font-mono font-semibold text-slate-900">{selectedTransaction.orderId}</p>
                  </div>

                  <div className="border-b border-slate-200 pb-3">
                    <p className="text-xs text-slate-600">Date & Time</p>
                    <p className="mt-1 font-semibold text-slate-900">{formatDate(selectedTransaction.createdAt)}</p>
                  </div>
                </div>

                {/* Payment Split Details */}
                <div className="mt-4">
                  <PaymentSplitInfo
                    amount={selectedTransaction.amount}
                    platformCommission={selectedTransaction.platformCommission}
                    creatorEarnings={selectedTransaction.creatorEarnings}
                    currency={selectedTransaction.currency}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardFrame>
  );
};

export default BuyerTransactionHistoryPage;
